/**
 * Customer Slide Upload - upload slide images directly via the browser.
 * Files <= 100 MB: single PUT. Files > 100 MB: multipart upload.
 * Supports choosing a whole folder (webkitdirectory); qualified files are queued.
 * MRXS directories are not packaged here — only discrete allowed file extensions.
 * Emits upload-activity sessions (basename + content hash prefix) for cloud visibility.
 * Shows per-file and overall upload percentage via XHR upload progress.
 * When the customer has multiple applications enabled, each file (or a folder default)
 * can select which application to run.
 */
import { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { authedGet, authedPost } from "../api/customerAuthedFetch";
import { CustomerNoticesContext } from "../CustomerLayout";
import PageTour from "../components/PageTour";
import SlideFormatHelp from "../components/SlideFormatHelp";
import InfoTip from "../components/InfoTip";
import { ThWithTip } from "../components/FieldTip";
import { SLIDES_UPLOAD_TOUR_STEPS } from "../tour/portalTour";
import adminStyles from "../ui/AdminStyles";
import { sha256PrefixOfBlob } from "../ui/sha256File";
import { useCustomerRoles } from "../CustomerRolesContext";
import { ROLE_IT, ROLE_OPERATIONS } from "../customerRoles";

const SINGLE_PUT_MAX_BYTES = 100 * 1024 * 1024; // 100 MB
const MULTIPART_PART_SIZE = 50 * 1024 * 1024; // 50 MB
const PART_UPLOAD_RETRIES = 3;
const HASH_PREFIX_LEN = 12;
const ALLOWED_EXTENSIONS = [".svs", ".ndpi", ".zip", ".tif", ".tiff", ".med"];
const ACCEPT_HINT = ALLOWED_EXTENSIONS.join(",");

const TIPS = {
  application:
    "Which catalog application will process this upload (for example AIxURO). When multiple are enabled, set a folder default or choose per file.",
  filePath: "Basename or relative path of the file queued for upload.",
  progress: "Upload progress for the current transfer (0–100%).",
  action: "Browse to pick a single file for this row, or clear a selected file.",
  folder:
    "Queue every matching slide file under a folder (including subfolders). MRXS directories are not packaged here—use a .zip or Smart Agent.",
  upload:
    "Start transferring queued files. Large files may use multipart upload. Progress appears in the Progress column.",
  cancel: "Stop the current upload run. Partial attempts are recorded in Upload Activity.",
};
// macOS maps accept= to UTIs; .svs/.ndpi are usually unregistered and get hidden
// in the open dialog (including search). Omit the filter there and validate in JS.
const USE_ACCEPT_FILTER = typeof navigator === "undefined" || !/Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);

function fileExtension(name) {
  const i = String(name || "").lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
}

function isAllowedSlideFile(file) {
  return ALLOWED_EXTENSIONS.includes(fileExtension(file?.name));
}

/** Prefer webkitRelativePath for folder picks; fall back to basename. */
function fileDisplayLabel(file) {
  const rel = (file?.webkitRelativePath || "").trim();
  return rel || file?.name || "";
}

/**
 * Folder picker exposes paths like "MyFolder/sub/file.svs" — first segment is the chosen folder name.
 * Returns null if unavailable.
 */
function folderNameFromFileList(fileList) {
  for (const file of Array.from(fileList || [])) {
    const rel = (file.webkitRelativePath || "").trim().replace(/\\/g, "/");
    if (!rel) continue;
    const first = rel.split("/").filter(Boolean)[0];
    if (first) return first;
  }
  return null;
}

/**
 * From a FileList (e.g. folder picker), keep allowed slide files only.
 * Rejects duplicate basenames — S3 keys are {company_id}/{filename}.
 */
function collectQualifiedFilesFromList(fileList) {
  const all = Array.from(fileList || []);
  const qualified = all.filter(isAllowedSlideFile);
  qualified.sort((a, b) =>
    fileDisplayLabel(a).localeCompare(fileDisplayLabel(b), undefined, {
      sensitivity: "base",
    })
  );
  const seen = new Map();
  const dupes = [];
  for (const f of qualified) {
    const base = f.name;
    if (seen.has(base)) {
      dupes.push(base);
    } else {
      seen.set(base, f);
    }
  }
  return {
    files: [...seen.values()],
    skipped: all.length - qualified.length,
    duplicateBasenames: [...new Set(dupes)],
  };
}

function newSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sess-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

class UploadCancelledError extends Error {
  constructor(message = "Upload cancelled") {
    super(message);
    this.name = "UploadCancelledError";
  }
}

function isCancelledError(err) {
  return (
    err instanceof UploadCancelledError ||
    err?.name === "AbortError" ||
    err?.name === "UploadCancelledError"
  );
}

function throwIfCancelled(signal) {
  if (signal?.aborted) throw new UploadCancelledError();
}

async function sha256Prefix(file, signal) {
  try {
    return await sha256PrefixOfBlob(file, HASH_PREFIX_LEN, { signal });
  } catch (err) {
    if (isCancelledError(err) || signal?.aborted || err?.name === "AbortError") {
      throw err instanceof Error ? err : new UploadCancelledError();
    }
    // Hashing must not block upload; activity can still correlate via session_id.
    console.warn("sha256Prefix failed", err);
    return null;
  }
}

async function postActivityEvents(events, signal) {
  if (!events.length) return { accepted: 0 };
  try {
    return await authedPost(
      "/customer/slides/upload-activity/batch",
      {
        channel: "portal",
        events,
      },
      signal
    );
  } catch (err) {
    if (isCancelledError(err) || signal?.aborted) {
      throw err instanceof Error ? err : new UploadCancelledError();
    }
    // Telemetry must not block upload UX for non-cancel failures
    return { accepted: 0 };
  }
}

/**
 * PUT body to a URL with upload progress (fetch cannot report upload progress reliably).
 * Pass AbortSignal to cancel via xhr.abort().
 * Returns { status, etag }.
 */
function putWithProgress(url, body, { contentType, onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadCancelledError());
      return;
    }

    const xhr = new XMLHttpRequest();
    const onAbortSignal = () => xhr.abort();

    const cleanup = () => {
      if (signal) signal.removeEventListener("abort", onAbortSignal);
    };

    xhr.open("PUT", url);
    if (contentType) {
      xhr.setRequestHeader("Content-Type", contentType);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && typeof onProgress === "function") {
        onProgress(e.loaded, e.total);
      }
    };
    xhr.onload = () => {
      cleanup();
      resolve({
        status: xhr.status,
        etag: xhr.getResponseHeader("ETag"),
      });
    };
    xhr.onerror = () => {
      cleanup();
      // Some browsers report abort as a network error instead of onabort.
      if (signal?.aborted) {
        reject(new UploadCancelledError());
        return;
      }
      reject(new Error("Network error during upload"));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new UploadCancelledError());
    };
    if (signal) signal.addEventListener("abort", onAbortSignal);
    xhr.send(body);
  });
}

function clampPct(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function defaultApplicationId(enabledApps) {
  const list = Array.isArray(enabledApps) ? enabledApps.filter(Boolean) : [];
  if (list.length === 0) return "AIxURO";
  if (list.includes("AIxURO")) return "AIxURO";
  return list[0];
}

function emptyRow(id, application = "") {
  return {
    id,
    file: null,
    sessionId: null,
    contentHashPrefix: null,
    relativePath: null,
    application,
  };
}

export default function CustomerSlideUpload() {
  const { blocking } = useContext(CustomerNoticesContext);
  const { hasAnyRole } = useCustomerRoles();
  const canSeeSmartAgent = hasAnyRole([ROLE_OPERATIONS, ROLE_IT]);
  const [enabledApplications, setEnabledApplications] = useState(["AIxURO"]);
  const [appsLoaded, setAppsLoaded] = useState(false);
  const [folderApplication, setFolderApplication] = useState("AIxURO");
  const [rows, setRows] = useState(() => [emptyRow(0, "AIxURO")]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  /** Display name of the last chosen folder (from webkitRelativePath), if any. */
  const [selectedFolderName, setSelectedFolderName] = useState(null);
  /** @type {[Record<string, number>, Function]} filename -> 0..100 */
  const [progressByName, setProgressByName] = useState({});
  const nextId = useRef(1);
  const inputRefs = useRef({});
  const folderInputRef = useRef(null);
  const progressRef = useRef({});
  /** @type {React.MutableRefObject<AbortController | null>} */
  const uploadAbortRef = useRef(null);
  /** Sessions for the in-flight upload run (for cancel telemetry). */
  const uploadMetaRef = useRef([]);
  /** Prevent duplicate cancelled batch posts for one run. */
  const cancelPostedRef = useRef(false);
  /** In-flight cancel telemetry promise (so catch can await the same post). */
  const cancelPostPromiseRef = useRef(null);

  useEffect(() => {
    document.title = "Slide Upload";
  }, []);

  useEffect(() => {
    let cancelled = false;
    authedGet("/customer/profile")
      .then((data) => {
        if (cancelled) return;
        const enabled = Array.isArray(data?.enabled_applications)
          ? data.enabled_applications.map(String).filter(Boolean)
          : ["AIxURO"];
        const list = enabled.length ? enabled : ["AIxURO"];
        const def = defaultApplicationId(list);
        setEnabledApplications(list);
        setFolderApplication(def);
        setRows((prev) =>
          prev.map((r) => ({
            ...r,
            application: r.application && list.includes(r.application) ? r.application : def,
          }))
        );
        setAppsLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setEnabledApplications(["AIxURO"]);
        setFolderApplication("AIxURO");
        setAppsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const multiApp = enabledApplications.length > 1;

  const setFileProgress = (filename, pct) => {
    const next = clampPct(pct);
    progressRef.current = { ...progressRef.current, [filename]: next };
    setProgressByName({ ...progressRef.current });
  };

  const postCancelledSessions = (metas) => {
    if (!metas?.length || cancelPostedRef.current) {
      return cancelPostPromiseRef.current || Promise.resolve({ accepted: 0 });
    }
    cancelPostedRef.current = true;
    // Do not pass the aborted upload signal — cancel telemetry must still reach the API.
    const promise = postActivityEvents(
      metas.map((m) => ({
        session_id: m.sessionId,
        status: "cancelled",
        display_name: m.displayName,
        content_hash_prefix: m.contentHashPrefix,
        size_bytes: m.file?.size ?? m.sizeBytes,
        application: m.application || undefined,
        reason: "cancelled by user",
      }))
    );
    cancelPostPromiseRef.current = promise;
    return promise;
  };

  const rotateSessionsAfterCancel = () => {
    // Cancelled sessions are terminal server-side; mint new ids so a retry is a new attempt.
    setRows((prev) =>
      prev.map((r) =>
        r.file
          ? {
              ...r,
              sessionId: newSessionId(),
              contentHashPrefix: r.contentHashPrefix,
            }
          : r
      )
    );
  };

  const handleFileChange = (rowId, e) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = "";
    if (!file) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? {
                ...r,
                file: null,
                sessionId: null,
                contentHashPrefix: null,
                relativePath: null,
              }
            : r
        )
      );
      return;
    }
    if (!isAllowedSlideFile(file)) {
      setError(
        `Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS.join(", ")} (including .svs and .ndpi).`
      );
      return;
    }
    setError(null);
    setSelectedFolderName(null);
    const sessionId = newSessionId();
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
              ...r,
              file,
              sessionId,
              contentHashPrefix: null,
              relativePath: null,
              application:
                r.application || folderApplication || defaultApplicationId(enabledApplications),
            }
          : r
      )
    );
    void emitDetected(rowId, file, sessionId, folderApplication || defaultApplicationId(enabledApplications));
  };

  const handleFolderChange = (e) => {
    // FileList is live — copy before clearing the input or length becomes 0.
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (list.length === 0) return;

    const folderName = folderNameFromFileList(list);
    const { files, skipped, duplicateBasenames } = collectQualifiedFilesFromList(list);
    if (duplicateBasenames.length > 0) {
      setError(
        `Folder has multiple files with the same name (uploads would overwrite): ${duplicateBasenames
          .slice(0, 5)
          .join(", ")}${duplicateBasenames.length > 5 ? "…" : ""}. ` +
          "Rename duplicates or upload files individually."
      );
      return;
    }
    if (files.length === 0) {
      setError(
        `No supported slide files in that folder. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}.` +
          (skipped > 0 ? ` (${skipped} other file(s) skipped.)` : "")
      );
      return;
    }

    setError(null);
    setSuccess(null);
    setSelectedFolderName(folderName);
    const appForFolder =
      folderApplication || defaultApplicationId(enabledApplications);
    setFolderApplication(appForFolder);
    const newRows = files.map((file) => {
      const id = nextId.current++;
      const sessionId = newSessionId();
      return {
        id,
        file,
        sessionId,
        contentHashPrefix: null,
        relativePath: fileDisplayLabel(file),
        application: appForFolder,
      };
    });
    setRows(newRows);
    for (const row of newRows) {
      void emitDetected(row.id, row.file, row.sessionId, row.application);
    }
    const skipNote =
      skipped > 0 ? ` Skipped ${skipped} non-matching file(s).` : "";
    const folderNote = folderName ? ` Folder: ${folderName}.` : "";
    setSuccess(`Queued ${files.length} file(s) from folder.${folderNote}${skipNote}`);
  };

  async function emitDetected(rowId, file, sessionId, application) {
    let contentHashPrefix = null;
    try {
      contentHashPrefix = await sha256Prefix(file);
    } catch {
      contentHashPrefix = null;
    }
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId && r.sessionId === sessionId
          ? { ...r, contentHashPrefix }
          : r
      )
    );
    await postActivityEvents([
      {
        session_id: sessionId,
        status: "detected",
        display_name: file.name,
        content_hash_prefix: contentHashPrefix,
        size_bytes: file.size,
        application: application || undefined,
      },
    ]);
  }

  const triggerFileInput = (rowId) => {
    inputRefs.current[rowId]?.click();
  };

  const triggerFolderInput = () => {
    folderInputRef.current?.click();
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      emptyRow(
        nextId.current++,
        folderApplication || defaultApplicationId(enabledApplications)
      ),
    ]);
  };

  const setRowApplication = (rowId, application) => {
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, application } : r))
    );
  };

  const applyFolderApplicationToAll = (application) => {
    setFolderApplication(application);
    setRows((prev) => prev.map((r) => ({ ...r, application })));
  };

  const handleUpload = async () => {
    const files = rows.filter((r) => r.file).map((r) => r.file);
    if (files.length === 0) {
      setError("Please select at least one file to upload.");
      return;
    }
    const invalid = files.find((f) => !isAllowedSlideFile(f));
    if (invalid) {
      setError(
        `Unsupported file type: ${invalid.name}. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}.`
      );
      return;
    }
    const missingApp = rows.find((r) => r.file && !String(r.application || "").trim());
    if (missingApp || (multiApp && rows.some((r) => r.file && !enabledApplications.includes(r.application)))) {
      setError(
        multiApp
          ? "Select an application for each file before uploading."
          : "Application is required for each file."
      );
      return;
    }

    uploadAbortRef.current?.abort();
    const abortController = new AbortController();
    uploadAbortRef.current = abortController;
    const { signal } = abortController;
    uploadMetaRef.current = [];
    cancelPostedRef.current = false;
    cancelPostPromiseRef.current = null;

    setError(null);
    setSuccess(null);
    setUploading(true);
    progressRef.current = {};
    setProgressByName({});

    const fileMeta = [];
    try {
      const selectedRows = rows.filter((r) => r.file);
      for (const row of selectedRows) {
        throwIfCancelled(signal);
        const f = row.file;
        const sessionId = row.sessionId || newSessionId();
        let contentHashPrefix = row.contentHashPrefix || null;
        if (!contentHashPrefix) {
          try {
            contentHashPrefix = await sha256Prefix(f, signal);
          } catch (err) {
            if (isCancelledError(err) || signal?.aborted) throw err;
            contentHashPrefix = null;
          }
        }
        throwIfCancelled(signal);
        fileMeta.push({
          file: f,
          sessionId,
          contentHashPrefix,
          displayName: f.name,
          application:
            row.application ||
            folderApplication ||
            defaultApplicationId(enabledApplications),
        });
        setFileProgress(f.name, 0);
      }

      // Available immediately for Cancel → activity telemetry (before / after started).
      uploadMetaRef.current = fileMeta;

      throwIfCancelled(signal);
      await postActivityEvents(
        fileMeta.map((m) => ({
          session_id: m.sessionId,
          status: "started",
          display_name: m.displayName,
          content_hash_prefix: m.contentHashPrefix,
          size_bytes: m.file.size,
          application: m.application || undefined,
        })),
        signal
      );

      const presignedBody = {
        channel: "portal",
        files: fileMeta.map((m) => ({
          filename: m.file.name,
          content_type: m.file.type || "application/octet-stream",
          size: m.file.size,
          session_id: m.sessionId,
          content_hash_prefix: m.contentHashPrefix,
          application: m.application || undefined,
        })),
      };
      throwIfCancelled(signal);
      const { uploads = [], multipart_uploads = [] } = await authedPost(
        "/customer/slides/upload/presigned",
        presignedBody,
        signal
      );

      const largeFiles = files.filter((f) => f.size >= SINGLE_PUT_MAX_BYTES);
      const largeInMultipart = new Set(multipart_uploads.map((m) => m.filename));
      for (const f of largeFiles) {
        if (!largeInMultipart.has(f.name)) {
          throw new Error(
            `File ${f.name} (${(f.size / 1e9).toFixed(1)} GB) requires multipart upload. ` +
              "Please deploy the latest backend (Lambda) and try again."
          );
        }
      }

      const s3_locations = [];
      const multipart_completions = [];
      const metaByName = Object.fromEntries(fileMeta.map((m) => [m.file.name, m]));

      for (let i = 0; i < uploads.length; i++) {
        throwIfCancelled(signal);
        const { upload_url, s3_uri, session_id: sid } = uploads[i];
        const meta = metaByName[uploads[i].filename];
        if (!meta) throw new Error(`File not found: ${uploads[i].filename}`);

        const putResp = await putWithProgress(upload_url, meta.file, {
          contentType: meta.file.type || "application/octet-stream",
          signal,
          onProgress: (loaded, total) => {
            setFileProgress(meta.file.name, (100 * loaded) / (total || meta.file.size || 1));
          },
        });

        if (putResp.status < 200 || putResp.status >= 300) {
          await postActivityEvents([
            {
              session_id: sid || meta.sessionId,
              status: "failed",
              display_name: meta.displayName,
              content_hash_prefix: meta.contentHashPrefix,
              size_bytes: meta.file.size,
              application: meta.application || undefined,
              error: `Upload failed: ${putResp.status}`,
            },
          ]);
          throw new Error(`Upload failed for ${meta.file.name}: ${putResp.status}`);
        }
        setFileProgress(meta.file.name, 100);
        s3_locations.push({
          s3_uri,
          session_id: sid || meta.sessionId,
          content_hash_prefix: meta.contentHashPrefix,
          application: meta.application,
        });
      }

      for (const mpu of multipart_uploads) {
        throwIfCancelled(signal);
        const meta = metaByName[mpu.filename];
        if (!meta) throw new Error(`File not found: ${mpu.filename}`);

        const parts = [];
        let completedBytes = 0;
        for (let i = 0; i < mpu.parts.length; i++) {
          throwIfCancelled(signal);
          const part = mpu.parts[i];
          const start = (part.part_number - 1) * MULTIPART_PART_SIZE;
          const end = Math.min(start + MULTIPART_PART_SIZE, meta.file.size);
          const blob = meta.file.slice(start, end);
          const partSize = end - start;

          let lastErr;
          for (let attempt = 1; attempt <= PART_UPLOAD_RETRIES; attempt++) {
            throwIfCancelled(signal);
            try {
              const putResp = await putWithProgress(part.upload_url, blob, {
                signal,
                onProgress: (loaded) => {
                  const pct =
                    (100 * (completedBytes + loaded)) / (meta.file.size || 1);
                  setFileProgress(meta.file.name, pct);
                },
              });

              if (putResp.status < 200 || putResp.status >= 300) {
                throw new Error(`Part ${part.part_number}: ${putResp.status}`);
              }

              const etag = putResp.etag;
              if (!etag) throw new Error(`No ETag for part ${part.part_number}`);
              parts.push({ part_number: part.part_number, etag });
              completedBytes += partSize;
              setFileProgress(
                meta.file.name,
                (100 * completedBytes) / (meta.file.size || 1)
              );
              lastErr = null;
              break;
            } catch (err) {
              if (isCancelledError(err)) throw err;
              lastErr = err;
              if (attempt < PART_UPLOAD_RETRIES) {
                await new Promise((r) => setTimeout(r, 1000 * attempt));
                throwIfCancelled(signal);
              }
            }
          }
          if (lastErr) {
            await postActivityEvents([
              {
                session_id: mpu.session_id || meta.sessionId,
                status: "failed",
                display_name: meta.displayName,
                content_hash_prefix: meta.contentHashPrefix,
                size_bytes: meta.file.size,
                application: meta.application || undefined,
                error: lastErr.message,
              },
            ]);
            throw new Error(
              `Upload failed for ${meta.file.name} part ${part.part_number} after ${PART_UPLOAD_RETRIES} retries: ${lastErr.message}`
            );
          }
        }

        setFileProgress(meta.file.name, 100);
        multipart_completions.push({
          s3_uri: mpu.s3_uri,
          upload_id: mpu.upload_id,
          parts,
          session_id: mpu.session_id || meta.sessionId,
          content_hash_prefix: meta.contentHashPrefix,
          application: meta.application,
        });
      }

      throwIfCancelled(signal);
      const completeBody = { channel: "portal" };
      if (s3_locations.length > 0) completeBody.s3_locations = s3_locations;
      if (multipart_completions.length > 0) completeBody.multipart_completions = multipart_completions;

      if (Object.keys(completeBody).length > 1) {
        await authedPost("/customer/slides/upload/complete", completeBody, signal);
      }

      setSuccess(`Successfully uploaded ${files.length} file(s).`);
      setSelectedFolderName(null);
      const def = folderApplication || defaultApplicationId(enabledApplications);
      setRows([emptyRow(nextId.current++, def)]);
      progressRef.current = {};
      setProgressByName({});
    } catch (err) {
      if (isCancelledError(err) || signal.aborted) {
        await postCancelledSessions(uploadMetaRef.current.length ? uploadMetaRef.current : fileMeta);
        rotateSessionsAfterCancel();
        setError(null);
        setSuccess("Upload cancelled.");
      } else {
        setError(err?.message || "Upload failed");
      }
    } finally {
      if (uploadAbortRef.current === abortController) {
        uploadAbortRef.current = null;
      }
      uploadMetaRef.current = [];
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    const metas =
      uploadMetaRef.current.length > 0
        ? [...uploadMetaRef.current]
        : rows
            .filter((r) => r.file && r.sessionId)
            .map((r) => ({
              sessionId: r.sessionId,
              contentHashPrefix: r.contentHashPrefix,
              displayName: r.file.name,
              file: r.file,
            }));
    uploadAbortRef.current?.abort();
    void postCancelledSessions(metas).then(rotateSessionsAfterCancel);
  };

  const selectedCount = rows.filter((r) => r.file).length;
  const overallPct = (() => {
    const names = rows.filter((r) => r.file).map((r) => r.file.name);
    if (!uploading || names.length === 0) return null;
    const sum = names.reduce((acc, n) => acc + (progressByName[n] ?? 0), 0);
    return clampPct(sum / names.length);
  })();

  if (blocking) {
    return (
      <div style={{ padding: 16, maxWidth: 800 }}>
        <h2 style={{ marginBottom: 8 }}>Slide Upload</h2>
        <div style={styles.errorBanner} role="alert">
          Browser slide upload is disabled during customer portal maintenance.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 800 }}>
      <div
        data-tour="slides-upload-intro"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: 8,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0 }}>Slide Upload</h2>
        <PageTour tourId="slides-upload" steps={SLIDES_UPLOAD_TOUR_STEPS} />
      </div>
      <p style={styles.hint} data-tour="slides-upload-hint">
        Accepted files: {ALLOWED_EXTENSIONS.join(", ")} (
        <SlideFormatHelp linkLabel="see supported formats" />
        ). Use <strong>Browse file</strong> for one file, or{" "}
        <strong>Choose folder</strong> to queue all matching files in a directory
        (including subfolders). MRXS folders are not supported here.
        {multiApp
          ? " Choose an application per file, or set a folder default that applies to all queued files."
          : null}
      </p>
      {canSeeSmartAgent ? (
        <p style={{ ...styles.hint, marginTop: 0 }}>
          Prefer folder watch or high-volume uploads?{" "}
          <Link to="/organization/software" style={{ color: "#2563eb" }}>
            Download Smart Agent
          </Link>{" "}
          from Organization → Software.
        </p>
      ) : null}

      {error && (
        <div style={styles.errorBanner} role="alert">
          {error}
        </div>
      )}
      {success && (
        <div style={styles.successBanner} role="status">
          {success}
        </div>
      )}
      <div data-tour="slides-upload-app">
        {selectedFolderName && (
          <div style={styles.folderBanner} role="status">
            <div>
              Selected folder: <strong>{selectedFolderName}</strong>
              {selectedCount > 0 ? (
                <span style={{ color: "#64748b" }}>
                  {" "}
                  ({selectedCount} file{selectedCount === 1 ? "" : "s"} queued)
                </span>
              ) : null}
            </div>
            {multiApp && appsLoaded ? (
              <label style={styles.folderAppLabel}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  Folder application
                  <InfoTip text={TIPS.application} />
                </span>
                <select
                  value={folderApplication}
                  disabled={uploading}
                  onChange={(e) => applyFolderApplicationToAll(e.target.value)}
                  style={styles.appSelect}
                >
                  {enabledApplications.map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        )}
        {!selectedFolderName && multiApp && appsLoaded ? (
          <div style={styles.defaultAppBanner}>
            <label style={styles.folderAppLabel}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                Default application for new files
                <InfoTip text={TIPS.application} />
              </span>
              <select
                value={folderApplication}
                disabled={uploading}
                onChange={(e) => setFolderApplication(e.target.value)}
                style={styles.appSelect}
              >
                {enabledApplications.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}
        {!multiApp ? (
          <div style={{ ...styles.defaultAppBanner, color: "#64748b", fontSize: 13 }}>
            Application for uploads: <strong>{folderApplication || "AIxURO"}</strong>
          </div>
        ) : null}
      </div>

      <input
        ref={(el) => {
          folderInputRef.current = el;
          if (el) {
            el.setAttribute("webkitdirectory", "");
            el.setAttribute("directory", "");
          }
        }}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={handleFolderChange}
      />

      <table style={adminStyles.table} data-tour="slides-upload-table">
        <thead>
          <tr>
            <th style={{ ...cellStyle, width: 48 }}>#</th>
            <ThWithTip tip={TIPS.filePath} style={cellStyle}>
              File path
            </ThWithTip>
            {multiApp ? (
              <ThWithTip tip={TIPS.application} style={{ ...cellStyle, width: 160 }}>
                Application
              </ThWithTip>
            ) : null}
            <ThWithTip tip={TIPS.progress} style={{ ...cellStyle, width: 160 }}>
              Progress
            </ThWithTip>
            <ThWithTip
              tip={TIPS.action}
              style={{ ...cellStyle, ...actionCellStyle, width: 120 }}
            >
              Action
            </ThWithTip>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const pct =
              row.file && uploading ? progressByName[row.file.name] : undefined;
            const label = row.relativePath || row.file?.name;
            return (
              <tr key={row.id} style={{ backgroundColor: idx % 2 ? "white" : "#fafafa" }}>
                <td style={cellStyle}>{idx + 1}</td>
                <td style={{ ...cellStyle, color: row.file ? "#111827" : "#9ca3af" }}>
                  {row.file ? (
                    <>
                      {label}
                      {row.file.size >= SINGLE_PUT_MAX_BYTES && (
                        <span style={{ color: "#6b7280", fontSize: 12, marginLeft: 8 }}>
                          (multipart)
                        </span>
                      )}
                    </>
                  ) : (
                    "No file selected"
                  )}
                </td>
                {multiApp ? (
                  <td style={cellStyle}>
                    <select
                      value={row.application || folderApplication}
                      disabled={uploading || !appsLoaded}
                      onChange={(e) => setRowApplication(row.id, e.target.value)}
                      style={styles.appSelect}
                      aria-label={`Application for row ${idx + 1}`}
                    >
                      {enabledApplications.map((id) => (
                        <option key={id} value={id}>
                          {id}
                        </option>
                      ))}
                    </select>
                  </td>
                ) : null}
                <td style={cellStyle}>
                  {row.file && uploading && pct != null ? (
                    <div style={styles.progressWrap} title={`${pct}%`}>
                      <div style={styles.progressTrack}>
                        <div
                          style={{
                            ...styles.progressFill,
                            width: `${pct}%`,
                          }}
                        />
                      </div>
                      <span style={styles.progressLabel}>{pct}%</span>
                    </div>
                  ) : (
                    <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
                  )}
                </td>
                <td style={{ ...cellStyle, ...actionCellStyle }}>
                  <input
                    ref={(el) => (inputRefs.current[row.id] = el)}
                    type="file"
                    {...(USE_ACCEPT_FILTER ? { accept: ACCEPT_HINT } : {})}
                    style={{ display: "none" }}
                    onChange={(e) => handleFileChange(row.id, e)}
                  />
                  <button
                    type="button"
                    style={styles.browseBtn}
                    onClick={() => triggerFileInput(row.id)}
                    disabled={uploading}
                  >
                    Browse file
                  </button>
                </td>
              </tr>
            );
          })}
          <tr style={{ backgroundColor: "#fafafa" }}>
            <td style={cellStyle} colSpan={multiApp ? 4 : 3} />
            <td style={{ ...cellStyle, ...actionCellStyle }}>
              <button
                type="button"
                data-tour="slides-upload-add-row"
                style={styles.addRowBtn}
                onClick={addRow}
                title="Add row"
                disabled={uploading}
              >
                +
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div style={styles.actions}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            data-tour="slides-upload-folder"
            style={styles.secondaryBtn}
            onClick={triggerFolderInput}
            disabled={uploading}
            title="Select a folder; all matching slide files will be queued"
          >
            Choose folder
          </button>
          <InfoTip text={TIPS.folder} />
        </span>
        <div style={styles.actionsRight} data-tour="slides-upload-submit">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <button
              type="button"
              style={styles.uploadBtn}
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading
                ? overallPct != null
                  ? `Uploading… ${overallPct}%`
                  : "Uploading…"
                : `Upload ${selectedCount > 0 ? `(${selectedCount})` : ""}`}
            </button>
            <InfoTip text={TIPS.upload} align="end" />
          </span>
          {uploading && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <button
                type="button"
                style={styles.cancelBtn}
                onClick={handleCancelUpload}
                title="Stop the current upload"
              >
                Cancel
              </button>
              <InfoTip text={TIPS.cancel} align="end" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const cellStyle = {
  borderRight: "1px solid #e5e7eb",
  padding: "12px 16px",
};

const actionCellStyle = {
  textAlign: "center",
};

const styles = {
  hint: {
    margin: "0 0 16px",
    fontSize: 13,
    color: "#6b7280",
  },
  errorBanner: {
    padding: "12px 16px",
    marginBottom: 16,
    borderRadius: 8,
    background: "#fef2f2",
    color: "#b91c1c",
    border: "1px solid #fecaca",
  },
  successBanner: {
    padding: "12px 16px",
    marginBottom: 16,
    borderRadius: 8,
    background: "#f0fdf4",
    color: "#166534",
    border: "1px solid #bbf7d0",
  },
  folderBanner: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 14px",
    marginBottom: 16,
    borderRadius: 8,
    background: "#f8fafc",
    color: "#0f172a",
    border: "1px solid #e2e8f0",
    fontSize: 14,
  },
  defaultAppBanner: {
    padding: "10px 14px",
    marginBottom: 16,
    borderRadius: 8,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
  },
  folderAppLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "#475569",
  },
  appSelect: {
    padding: "4px 8px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "#fff",
    minWidth: 120,
  },
  browseBtn: {
    padding: "4px 10px",
    fontSize: 12,
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "#fff",
    cursor: "pointer",
  },
  secondaryBtn: {
    padding: "8px 16px",
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#111827",
    cursor: "pointer",
  },
  addRowBtn: {
    width: 28,
    height: 28,
    padding: 0,
    fontSize: 18,
    fontWeight: 400,
    lineHeight: 1,
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 16,
    alignItems: "center",
  },
  actionsRight: {
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  uploadBtn: {
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 8,
    border: "none",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
  },
  cancelBtn: {
    padding: "8px 16px",
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 8,
    border: "1px solid #fca5a5",
    background: "#fff",
    color: "#b91c1c",
    cursor: "pointer",
  },
  progressWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  progressTrack: {
    flex: 1,
    height: 8,
    borderRadius: 4,
    background: "#e5e7eb",
    overflow: "hidden",
    minWidth: 72,
  },
  progressFill: {
    height: "100%",
    background: "#2563eb",
    borderRadius: 4,
    transition: "width 0.15s ease-out",
  },
  progressLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
    minWidth: 36,
    textAlign: "right",
  },
};
